import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { layers } from '@atlaskit/theme/constants';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useApprovals,
	useApprovalsDispatch,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_ANALYTICS_SOURCE,
	getFailureStatusCode,
} from '@confluence/approvals/entry-points/analytics.ts';
import { ChangeStatusAndCancelApprovalModal } from '@confluence/approvals/entry-points/ChangeStatusAndCancelApprovalModal.ts';
import { CancelApprovalModal } from '@confluence/approvals/entry-points/CancelApprovalModal.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import {
	APPROVALS_PANE_ID,
	openApprovalsPaneForContext,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useUserProfileData } from '../hooks/useUserProfileData.ts';
import { colorCodeToColorName, tokenToHex } from '../helpers.ts';
import { ContentStateInfoPopup } from '../ContentStateInfoPopup/ContentStateInfoPopup.tsx';

import { ContentStateSkeleton } from './ContentStateSkeleton.tsx';
import { ContentStateButton } from './ContentStateButton.tsx';
import { ContentStateMenu } from './ContentStateMenu.tsx';
import { ContentStateMenuV2 } from './ContentStateMenuV2/ContentStateMenuV2.tsx';
import {
	createNewCustomStatesArrayWhenNewCustomAdded,
	createNewCustomStatesArrayWhenExistingCustomSelected,
	isDuplicate,
} from './helpers.ts';
import type { ContentState, EditorContentStateProps } from './types.ts';
import { i18n } from './i18n.ts';
import EditorContentStateQuery from './__generated__/EditorContentStateQuery.graphql.ts';
import type {
	EditorContentStateComponentSetContentStateMutation as EditorContentStateComponentSetContentStateMutation$data,
	EditorContentStateComponentSetContentStateMutationVariables as EditorContentStateComponentSetContentStateMutation$variables,
} from './__types__/EditorContentStateComponentSetContentStateMutation.ts';
import type {
	EditorContentStateComponentRemoveContentStateMutation as EditorContentStateComponentRemoveContentStateMutation$data,
	EditorContentStateComponentRemoveContentStateMutationVariables as EditorContentStateComponentRemoveContentStateMutation$variables,
} from './__types__/EditorContentStateComponentRemoveContentStateMutation.ts';
import type {
	EditorContentStateComponentSetContentStateAndPublishMutation$data,
	EditorContentStateComponentSetContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentSetContentStateAndPublishMutation.graphql.ts';
import type {
	EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
	EditorContentStateComponentRemoveContentStateAndPublishMutation$variables,
} from './__generated__/EditorContentStateComponentRemoveContentStateAndPublishMutation.graphql.ts';

export const EditorContentStateComponentImpl: FC<EditorContentStateProps> = ({
	contentId,
	contentType,
	contentStateState,
	userOffline,
	flags,
	forceContentStatePlacementToTop,
	onUpdateSuccess,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
	isViewMode,
	canEdit = true,
}) => {
	const contentStateButtonRef = useRef(null);
	const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState<boolean>(false);
	const [isShowingInfoPopup, setIsShowingInfoPopup] = useState<boolean>(false);
	const [isCreatingContentState, setIsCreatingContentState] = useState<boolean>(false);
	const [selectedColor, setSelectedColor] = useState<string | null>(null);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false);
	const [isHoveringRemove, setIsHoveringRemove] = useState<boolean>(false);
	const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);

	const originalContentState = useRef(contentStateState.state.contentState);

	// Track if we're creating a NEW custom status (V1) vs selecting existing or V2 optimistic
	// Used to determine if onCompleted should update customStatesArray
	const isCreatingNewCustomStatusRef = useRef<boolean>(false);

	const setIsContentStateMenuOpenWithListener = useCallback(
		(newValue: boolean) => {
			onContentStateMenuToggle?.(newValue);
			setIsContentStateMenuOpen(newValue);
		},
		[onContentStateMenuToggle],
	);

	const isLivePage = useIsCurrentPageLive();
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const contentState = contentStateState.state.contentState;
	const contentStateLastUpdated = contentStateState.state.contentStateLastUpdated;

	// Approvals context hooks (safe to call unconditionally — returns defaults when provider absent)
	const {
		availableWorkflowStatuses,
		approvalRequest,
		approvalRequestLoading,
		otherModeReviewStatus,
	} = useApprovals();
	const {
		createWorkflowApplication,
		cancelWorkflowApplication,
		clearApprovalRequestOptimistically,
	} = useApprovalsDispatch();

	const activeWorkflowStatus = useMemo((): WorkflowStatus | null => {
		const activeStatusId = approvalRequest?.application?.workflowStatusId;
		if (!activeStatusId) return null;
		return availableWorkflowStatuses.find((s) => s.id === activeStatusId) ?? null;
	}, [approvalRequest, availableWorkflowStatuses]);

	// Memoize the review-eligible statuses
	const reviewEligibleStatuses = useMemo(
		() => availableWorkflowStatuses.filter((s) => s.allowsReviews),
		[availableWorkflowStatuses],
	);

	const respondedApproverData = useMemo(() => {
		if (!approvalRequest?.review?.approvers) return [];
		return approvalRequest.review.approvers.reduce<
			Array<{ name: string; src?: string; email?: string }>
		>((acc, a) => {
			if (a.status !== null && a.status !== 'PENDING_REVIEW') {
				acc.push({ name: a.name, src: a.avatarUrl, email: a.email });
			}
			return acc;
		}, []);
	}, [approvalRequest]);

	// Fetch user data for content state (skip when workflow status is active — that data comes from ApprovalsContext)
	const shouldFetchUserData =
		!activeWorkflowStatus &&
		contentState?.lastModifierAaid &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);
	const { updatedBy: contentStateUpdatedBy, loading: contentStateUserLoading } = useUserProfileData(
		shouldFetchUserData ? contentState.lastModifierAaid : null,
	);

	// Resolve update metadata: workflow status data from ApprovalsContext,
	// content state data from the query above
	const updatedBy = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedBy
		: contentStateUpdatedBy;
	const updatedAt = activeWorkflowStatus
		? approvalRequest?.application?.applicationLastModifiedDate || undefined
		: (contentStateLastUpdated ?? undefined);
	const userDataLoading = activeWorkflowStatus ? false : contentStateUserLoading;

	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(() => {
		return (
			!localStorage?.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		);
	});
	const [removeContentStateMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateMutation$data,
		EditorContentStateComponentRemoveContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateMutation($contentId: ID!) {
				removeContentState(contentId: $contentId)
			}
		`,
		{
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
			onCompleted: () => {
				onUpdateSuccess?.();
			},
		},
	);
	const [setContentStateMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateMutation$data,
		EditorContentStateComponentSetContentStateMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				// Only update customStatesArray when creating a NEW custom status (V1 flow)
				// V2 handles this optimistically before the mutation
				if (data.setContentState && isCreatingNewCustomStatusRef.current) {
					const newCustomStatesArray = createNewCustomStatesArrayWhenNewCustomAdded(
						contentStateState.state.customStatesArray,
						data.setContentState,
					);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentState) {
					originalContentState.current = data.setContentState;
				}
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
						shouldSkipContentStateLastUpdated: expValEquals(
							'confluence_approvals_eap',
							'isEnabled',
							true,
						)
							? false
							: !isLivePage,
						isApprovalsFgOn: expValEquals('confluence_approvals_eap', 'isEnabled', true),
						format: expValEquals('confluence_approvals_eap', 'isEnabled', true)
							? 'MILLIS'
							: 'USER_FRIENDLY',
					},
				},
			],
		},
	);

	const [setContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentSetContentStateAndPublishMutation$data,
		EditorContentStateComponentSetContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentSetContentStateAndPublishMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
				$suppressDraftPropertyUpdate: Boolean
				$cloudId: ID!
			) {
				setContentStateAndPublish(
					cloudId: $cloudId
					contentId: $contentId
					contentState: $contentStateInput
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				isCreatingNewCustomStatusRef.current = false;
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'setContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				if (data.setContentStateAndPublish && isCreatingNewCustomStatusRef.current) {
					const newCustomStatesArray = createNewCustomStatesArrayWhenNewCustomAdded(
						contentStateState.state.customStatesArray,
						data.setContentStateAndPublish,
					);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					isCreatingNewCustomStatusRef.current = false;
				}
				if (data.setContentStateAndPublish) {
					originalContentState.current = data.setContentStateAndPublish;
				}
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
		},
	);

	const [removeContentStateAndPublishMutation] = useMutationInterop<
		EditorContentStateComponentRemoveContentStateAndPublishMutation$data,
		EditorContentStateComponentRemoveContentStateAndPublishMutation$variables
	>(
		graphql`
			mutation EditorContentStateComponentRemoveContentStateAndPublishMutation(
				$contentId: ID!
				$doPublish: Boolean
				$suppressDraftPropertyUpdate: Boolean
			) {
				removeContentState(
					contentId: $contentId
					doPublish: $doPublish
					suppressDraftPropertyUpdate: $suppressDraftPropertyUpdate
				)
			}
		`,
		{
			onError: () => {
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-remove-error-flag',
					title: <FormattedMessage {...i18n.removeErrorFlagTitle} />,
					description: <FormattedMessage {...i18n.removeErrorFlagBody} />,
				});
				// TODO: Add robust error handling
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'contentState',
						actionSubjectId: 'removeContentStateAndPublish',
						source: 'editorContentState',
						attributes: {
							contentId,
							contentStatus: 'CURRENT',
							isLivePage,
						},
					},
				}).fire();
			},
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: false,
						shouldSkipContentStateLastUpdated: false,
						isApprovalsFgOn: true,
						format: 'MILLIS',
					},
				},
			],
			onCompleted: () => {
				onUpdateSuccess?.();
			},
		},
	);

	const activeRemoveMutation =
		isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)
			? removeContentStateAndPublishMutation
			: removeContentStateMutation;

	const rightSidebarContext = useRightSidebarContext();
	const isApprovalsPaneOpen = rightSidebarContext.getCurrent()?.panelId === APPROVALS_PANE_ID;
	const wasApprovalsPaneOpenRef = useRef(isApprovalsPaneOpen);

	// Close the content state info popup only at the moment the approvals pane opens,
	// not continuously while it is open — users should still be able to reopen the popup.
	// TODO: revisit this logic to see if it can be simplified.
	useEffect(() => {
		if (isApprovalsPaneOpen && !wasApprovalsPaneOpenRef.current && isShowingInfoPopup) {
			setIsShowingInfoPopup(false);
		}
		wasApprovalsPaneOpenRef.current = isApprovalsPaneOpen;
	}, [isApprovalsPaneOpen, isShowingInfoPopup]);

	// Reset color picker when menu closes
	useEffect(() => {
		if (!isContentStateMenuOpen) {
			setSelectedColor(null);
			setIsColorPickerOpen(false);
		}
	}, [isContentStateMenuOpen]);

	const applyExistingStatus = useCallback(
		(selectedStatus: ContentState) => {
			if (isViewMode && !cloudId) {
				return;
			}
			isCreatingNewCustomStatusRef.current = false;
			void contentStateState.setIsUpdating(true);
			if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				void setContentStateAndPublishMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
						suppressDraftPropertyUpdate: true,
						cloudId,
					},
				});
			} else {
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							color: tokenToHex(selectedStatus.color),
							name: selectedStatus.name,
							id: selectedStatus.id as any,
						},
					},
				});
			}
			void contentStateState.setContentState(selectedStatus);

			const customStatesArray = contentStateState.state.customStatesArray;
			const customIndex = customStatesArray.findIndex(
				(s) => s !== null && s.id === selectedStatus.id,
			);
			if (customIndex !== -1) {
				const newCustomStatesArray = createNewCustomStatesArrayWhenExistingCustomSelected(
					customStatesArray,
					selectedStatus,
					customIndex,
				);
				void contentStateState.setCustomStatesArray(newCustomStatesArray);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			const isCustom = customIndex !== -1;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: isCustom ? 'custom' : 'suggested',
						color: colorCodeToColorName(selectedStatus.color?.toUpperCase() || ''),
						restrictionLevel: selectedStatus.restrictionLevel?.toUpperCase(),
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const applyCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			if (isViewMode && !cloudId) {
				return;
			}
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: colorId.toLowerCase(),
					name: statusName,
				})
			) {
				const newContentState = { color: colorId, name: statusName };
				void contentStateState.setIsUpdating(true);
				isCreatingNewCustomStatusRef.current = true;

				const optimisticCustomState = {
					...newContentState,
					id: `temp-${Date.now()}`,
				};
				const updatedCustomStatesArray = [
					optimisticCustomState,
					...contentStateState.state.customStatesArray,
				];
				void contentStateState.setCustomStatesArray(updatedCustomStatesArray);

				if (isViewMode && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
					void setContentStateAndPublishMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
							suppressDraftPropertyUpdate: true,
							cloudId,
						},
					});
				} else {
					void setContentStateMutation({
						variables: {
							contentId,
							contentStateInput: {
								...newContentState,
								color: tokenToHex(colorId),
							},
						},
					});
				}
				void contentStateState.setContentState(newContentState);
			}

			if (!isViewMode && shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(colorId),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			shouldSetAutomationFlag,
			setContentStateAndPublishMutation,
			setContentStateMutation,
			cloudId,
		],
	);

	const handleSelectExistingStatusV2 = useCallback(
		(selectedStatus: ContentState) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyExistingStatus(selectedStatus);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyExistingStatus,
		],
	);

	const handleSaveCustomStatusV2 = useCallback(
		(statusName: string, colorId: string) => {
			if (
				activeWorkflowStatus &&
				approvalRequest?.review &&
				approvalRequest.review.reviewStatus === 'PENDING'
			) {
				setShowChangeStatusModal(true);
				return;
			}

			if (activeWorkflowStatus) {
				clearApprovalRequestOptimistically();
				void cancelWorkflowApplication();
			}

			applyCustomStatus(statusName, colorId);
		},
		[
			activeWorkflowStatus,
			approvalRequest,
			cancelWorkflowApplication,
			clearApprovalRequestOptimistically,
			applyCustomStatus,
		],
	);

	const applyRemove = useCallback(() => {
		isCreatingNewCustomStatusRef.current = false;
		if (contentStateState.state.contentState) {
			void activeRemoveMutation({
				variables: {
					contentId,
					...(isViewMode &&
						expValEquals('confluence_approvals_eap', 'isEnabled', true) && {
							doPublish: true,
							suppressDraftPropertyUpdate: true,
						}),
				},
			});
			void contentStateState.setContentState(null);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateRemoved',
					source: 'editorContentState',
					attributes: {
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
		}
	}, [
		contentStateState,
		activeRemoveMutation,
		contentId,
		createAnalyticsEvent,
		isLivePage,
		isViewMode,
	]);

	const handleRemoveV2 = useCallback(() => {
		if (
			activeWorkflowStatus &&
			approvalRequest?.review &&
			approvalRequest.review.reviewStatus === 'PENDING'
		) {
			setShowChangeStatusModal(true);
			return;
		}

		if (activeWorkflowStatus) {
			clearApprovalRequestOptimistically();
			void cancelWorkflowApplication();
		}

		setIsContentStateMenuOpenWithListener(false);
		applyRemove();
	}, [
		activeWorkflowStatus,
		approvalRequest,
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
		applyRemove,
	]);

	const handleSelectWorkflowStatus = useCallback(
		async (selectedStatus: WorkflowStatus) => {
			const previousContentState = contentStateState.state.contentState;
			const prevStateId = activeWorkflowStatus?.id || contentState?.id || null;
			const analyticsAttributes = {
				contentId,
				contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
				prevStateId,
				newStateId: selectedStatus.id,
				isNewStatusWorkflow: true,
			};

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'inReviewStatus',
					source: APPROVALS_ANALYTICS_SOURCE,
					attributes: analyticsAttributes,
				},
			}).fire();

			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);

			try {
				const result = await createWorkflowApplication(selectedStatus);

				if (otherModeReviewStatus === 'PENDING') {
					// Other mode already has an active pending review — show the info popup
					// (which renders the otherModeReviewWarning SectionMessage) instead of
					// opening the ApprovalsPane / StartApprovalPane.
					setIsShowingInfoPopup(true);
				} else {
					openApprovalsPaneForContext({
						contentId,
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						rightSidebarContext,
					});
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'created',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: { ...analyticsAttributes, applicationId: result.applicationId },
					},
				}).fire();
			} catch (error) {
				void contentStateState.setContentState(previousContentState);
				void flags.showErrorFlag({
					id: 'workflow-status-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'failed',
						actionSubject: 'approvalWorkflowApplication',
						source: APPROVALS_ANALYTICS_SOURCE,
						attributes: {
							...analyticsAttributes,
							failureStatusCode: getFailureStatusCode(error),
						},
					},
				}).fire();
			}
		},
		[
			otherModeReviewStatus,
			createWorkflowApplication,
			contentStateState,
			contentState,
			contentId,
			isViewMode,
			activeWorkflowStatus,
			createAnalyticsEvent,
			flags,
			setIsContentStateMenuOpenWithListener,
			rightSidebarContext,
		],
	);

	const handleColorPickerToggle = useCallback(() => {
		setIsColorPickerOpen((prev) => !prev);
	}, []);

	const handleColorPickerClose = useCallback(() => {
		setIsColorPickerOpen(false);
	}, []);

	const handleSelectColor = useCallback((newColor: string) => {
		setSelectedColor(newColor);
		setIsColorPickerOpen(false);
	}, []);

	const handleFocusTextField = useCallback(() => {
		setIsCreatingContentState(true);
	}, []);

	const handleMouseEnterRemove = useCallback(() => {
		setIsHoveringRemove(true);
	}, []);

	const handleMouseLeaveRemove = useCallback(() => {
		setIsHoveringRemove(false);
	}, []);

	const handleContentStatePopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsCreatingContentState(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleSubmit = useCallback(
		(name: string) => {
			if (
				!isDuplicate(contentStateState.state.contentState, {
					color: selectedColor?.toLowerCase() ?? null,
					name,
				})
			) {
				const newContentState = { color: selectedColor, name };
				void contentStateState.setIsUpdating(true);
				// Mark that we're creating a new custom status so onCompleted updates the array
				isCreatingNewCustomStatusRef.current = true;
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							...newContentState,
							color: tokenToHex(selectedColor),
						},
					},
				});
				void contentStateState.setContentState(newContentState);
			}
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(selectedColor ?? ''),
						restrictionLevel: 'NONE',
						uiLocation: 'pageTitleSection',
						contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
						isLivePage,
					},
				},
			}).fire();
			setIsCreatingContentState(false);
			setIsContentStateMenuOpenWithListener(false);
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			isLivePage,
			isViewMode,
			selectedColor,
			setContentStateMutation,
			setIsContentStateMenuOpenWithListener,
			shouldSetAutomationFlag,
		],
	);

	const handleCancel = useCallback(() => {
		setIsCreatingContentState(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateCancel',
				source: 'editorContentState',
				attributes: {
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [contentId, createAnalyticsEvent, isLivePage, isViewMode]);

	const handleRemove = useCallback(() => {
		// check if state exists before mutating since the trash button still shows if the feature is disabled
		// (page might not have a content state)
		if (contentStateState.state.contentState) {
			void removeContentStateMutation({
				variables: {
					contentId,
				},
			});
			setIsCreatingContentState(false);
			setIsHoveringRemove(false);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateRemoved',
				source: 'editorContentState',
				attributes: {
					uiLocation: 'pageTitleSection',
					contentStatus: isViewMode ? 'CURRENT' : 'DRAFT',
					isLivePage,
				},
			},
		}).fire();
	}, [
		contentId,
		contentStateState,
		createAnalyticsEvent,
		removeContentStateMutation,
		setIsCreatingContentState,
		setIsContentStateMenuOpenWithListener,
		isLivePage,
		isViewMode,
	]);

	const handlePopupToggle = useCallback(() => {
		setIsContentStateMenuOpenWithListener(!isContentStateMenuOpen);
	}, [isContentStateMenuOpen, setIsContentStateMenuOpenWithListener]);

	const handleInfoPopupClose = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);

	const handleChangeStatus = useCallback(() => {
		setIsShowingInfoPopup(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);

	const handleClearRequest = useCallback(() => {
		setIsShowingInfoPopup(false);
		setShowCancelApprovalModal(true);
	}, []);

	const handleConfirmClearRequest = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseCancelApprovalModal = useCallback(() => {
		setShowCancelApprovalModal(false);
		setIsShowingInfoPopup(true);
	}, []);

	const handleConfirmCancelApproval = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(false);

		clearApprovalRequestOptimistically();
		void cancelWorkflowApplication();
	}, [
		clearApprovalRequestOptimistically,
		cancelWorkflowApplication,
		setIsContentStateMenuOpenWithListener,
	]);

	const handleCloseChangeStatusModal = useCallback(() => {
		setShowChangeStatusModal(false);
		setIsContentStateMenuOpenWithListener(true);
	}, [setIsContentStateMenuOpenWithListener]);

	const togglePopup = useCallback(() => {
		const shouldShowInfoPopup =
			activeWorkflowStatus !== null && expValEquals('confluence_approvals_eap', 'isEnabled', true);
		if (shouldShowInfoPopup) {
			if (isShowingInfoPopup) {
				setIsShowingInfoPopup(false);
			} else {
				setIsShowingInfoPopup(true);
			}
		} else if (canEdit) {
			// Only allow opening the content state menu if the user has edit permission
			handlePopupToggle();
		}
		onClick?.();
	}, [activeWorkflowStatus, isShowingInfoPopup, handlePopupToggle, onClick, canEdit]);

	const renderTrigger = useCallback(
		(triggerProps: any) => (
			<ContentStateButton
				triggerProps={{ ...triggerProps }}
				togglePopup={togglePopup}
				contentState={contentStateState.state.contentState}
				activeWorkflowStatus={activeWorkflowStatus}
				isSelected={isContentStateMenuOpen}
				isWarning={isHoveringRemove}
				userOffline={userOffline}
				contentStateButtonRef={contentStateButtonRef}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				renderPlusButtonEmptyState={renderPlusButtonEmptyState}
				isDetailsPanel={isDetailsPanel}
			/>
		),
		[
			togglePopup,
			contentStateState.state.contentState,
			activeWorkflowStatus,
			isContentStateMenuOpen,
			isHoveringRemove,
			userOffline,
			floatingToolbarButtonStyle,
			renderPlusButtonEmptyState,
			isDetailsPanel,
		],
	);

	if (approvalRequestLoading && expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
		return <ContentStateSkeleton />;
	}

	return (
		<>
			<Popup
				testId="content-state-popup"
				isOpen={isContentStateMenuOpen || isShowingInfoPopup}
				onClose={handleInfoPopupClose}
				placement={forceContentStatePlacementToTop ? 'top-end' : 'bottom-start'}
				zIndex={layers.dialog()}
				trigger={renderTrigger}
				content={({ update }) => {
					if (isShowingInfoPopup) {
						return (
							<ContentStateInfoPopup
								contentState={contentState}
								activeWorkflowStatus={activeWorkflowStatus}
								updatedAt={updatedAt}
								updatedBy={updatedBy}
								loading={userDataLoading}
								onChangeStatus={handleChangeStatus}
								onClearRequest={handleClearRequest}
								canEdit={canEdit}
								contentType={contentType}
								isViewMode={isViewMode}
							/>
						);
					}

					if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
						return (
							<ContentStateMenuV2
								contentId={contentId}
								contentStateState={contentStateState}
								contentType={contentType}
								isLivePage={isLivePage}
								defaultStatesArray={contentStateState.state.defaultStatesArray || []}
								customStatesArray={contentStateState.state.customStatesArray || []}
								isContentStateEnabled={contentStateState.state.isContentStateEnabled}
								userOffline={userOffline}
								onSelectExistingStatus={handleSelectExistingStatusV2}
								onSaveCustomStatus={handleSaveCustomStatusV2}
								onRemove={handleRemoveV2}
								isContentStateMenuOpen={isContentStateMenuOpen}
								setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
								workflowStatuses={reviewEligibleStatuses}
								onSelectWorkflowStatus={handleSelectWorkflowStatus}
							/>
						);
					}

					return (
						<ContentStateMenu
							contentId={contentId}
							contentStateState={contentStateState}
							contentType={contentType}
							isContentStateEnabled={contentStateState.state.isContentStateEnabled}
							isSuggestedEnabled={contentStateState.state.isSuggestedEnabled}
							isCustomEnabled={contentStateState.state.isCustomEnabled}
							customStatesArray={contentStateState.state.customStatesArray}
							defaultStatesArray={contentStateState.state.defaultStatesArray}
							isCreatingContentState={isCreatingContentState}
							selectedColor={selectedColor}
							onSelectColor={handleSelectColor}
							isColorPickerOpen={isColorPickerOpen}
							onColorPickerToggle={handleColorPickerToggle}
							onColorPickerClose={handleColorPickerClose}
							onPopupClose={handleContentStatePopupClose}
							onSubmit={handleSubmit}
							onCancel={handleCancel}
							onRemove={handleRemove}
							onFocusTextField={handleFocusTextField}
							onMouseEnterRemove={handleMouseEnterRemove}
							onMouseLeaveRemove={handleMouseLeaveRemove}
							userOffline={userOffline}
							contentStateButtonRef={contentStateButtonRef}
							onUpdateSuccess={onUpdateSuccess}
							isContentStateMenuOpen={isContentStateMenuOpen}
							setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
							updatePopup={update}
						/>
					);
				}}
			/>
			{showChangeStatusModal && (
				<ChangeStatusAndCancelApprovalModal
					approverData={respondedApproverData}
					onCancelApproval={handleConfirmCancelApproval}
					onClose={handleCloseChangeStatusModal}
				/>
			)}
			{showCancelApprovalModal && (
				<CancelApprovalModal
					approverData={respondedApproverData}
					onCancelApproval={handleConfirmClearRequest}
					onClose={handleCloseCancelApprovalModal}
				/>
			)}
		</>
	);
};

export const EditorContentStateComponent = withFlags(EditorContentStateComponentImpl);
